const normalize = value => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();

const performerNames = registration => (registration?.performers || []).map(performer =>
    typeof performer === 'string' ? performer.trim()
        : (performer?.fullName || `${performer?.firstName || ''} ${performer?.lastName || ''}`.trim() || performer?.name || '')
).filter(Boolean).join(', ');

const seatLabel = seat => seat.seatLabel || `${seat.row || ''}${seat.number || ''}`;

// Keep performance assignments separate from purchased tickets: an assignment is not admission proof.
export const buildSeatOccupancyLookup = ({ sessions, registrations, assignments, bookings, seats }) => {
    const registrationsById = new Map(registrations.map(registration => [registration.id, registration]));
    const bookingsById = new Map(bookings.map(booking => [booking.id, booking]));
    const rows = [];

    sessions.forEach(session => {
        const sessionSeats = seats.filter(seat => seat.venueId === session.venueId && seat.sessionId === session.sessionId
            && ['booked', 'reserved', 'locked'].includes(seat.status));
        const sessionBookings = bookings.filter(booking => booking.venue === session.venueId
            && booking.date === session.date && booking.session === session.time
            && !booking.isOrchestra && booking.bookingType !== 'public_orchestra'
            && normalize(booking.paymentStatus) !== 'archived_test');

        (assignments[session.key] || []).forEach(entry => {
            const registration = registrationsById.get(entry.registrantId);
            const name = performerNames(registration) || entry.name || 'Unknown performer';
            const assignedSeats = sessionSeats.filter(seat => !seat.bookingId && !seat.lockedByBookingId
                && (seat.assignedTo?.registrantId === entry.registrantId || seat.lockedBy?.registrantId === entry.registrantId));
            rows.push({
                ...session, key: `registrant:${session.key}:${entry.registrantId}`,
                personType: 'Performer assignment', name, reservationName: '-', reference: entry.registrantId,
                seats: assignedSeats.map(seatLabel).filter(Boolean).join(', ') || 'No numbered seat recorded',
                status: 'Performance assigned',
                searchText: normalize([name, registration?.name, registration?.teacherName, entry.registrantId].join(' ')),
            });
        });

        sessionBookings.forEach(booking => {
            const registrationName = performerNames(registrationsById.get(booking.registrantId)) || booking.registrantName || '';
            const reservationName = booking.buyerName || booking.userName || 'Unknown reservation name';
            const ownedSeats = sessionSeats.filter(seat => seat.bookingId === booking.id || seat.lockedByBookingId === booking.id);
            // Do not revive old selected labels after cancellation or reallocation.
            const labels = ownedSeats.map(seatLabel);
            const status = String(booking.paymentStatus || 'unknown').toUpperCase();
            rows.push({
                ...session, key: `booking:${booking.id}`,
                personType: booking.bookingType === 'winner' ? 'Winner reservation' : 'Public reservation',
                name: registrationName || '-', reservationName, reference: booking.id,
                seats: labels.filter(Boolean).join(', ') || (status === 'PAID' ? 'Awaiting seat assignment' : 'No numbered seat assigned'),
                status,
                searchText: normalize([registrationName, reservationName, booking.userName, booking.buyerName, booking.id, booking.registrantId].join(' ')),
            });
        });

        // Legacy seat holders can exist without a public booking or a current performance assignment.
        sessionSeats.forEach(seat => {
            if (bookingsById.has(seat.bookingId || seat.lockedByBookingId)) return;
            const holder = seat.assignedTo || seat.lockedBy;
            if (!holder) return;
            if (!seat.bookingId && !seat.lockedByBookingId
                && (assignments[session.key] || []).some(entry => entry.registrantId === holder.registrantId)) return;
            const name = holder.registrantName || '-';
            const reservationName = holder.userName || '-';
            rows.push({
                ...session, key: `seat:${seat.id}`, personType: 'Legacy seat holder', name, reservationName,
                reference: seat.bookingId || seat.lockedByBookingId || holder.registrantId || seat.id,
                seats: seatLabel(seat) || 'No seat label recorded', status: String(seat.status).toUpperCase(),
                searchText: normalize([name, reservationName, holder.registrantId].join(' ')),
            });
        });
    });
    return rows;
};

export const matchesSeatOccupancySearch = (row, search) => {
    const terms = normalize(search).split(' ').filter(Boolean);
    return terms.every(term => row.searchText.includes(term));
};
